import { Form, Input, InputRef } from 'antd';
import Modal from 'components/Modal';
import NumberPadV2 from 'components/NumberKeyBoard/Currency/NumberPadV2';
import Text from 'components/Text';
import PrintBodyCouponOnly from 'features/payment/pages/PrintPage/components/PrintBodyCouponOnly';
import userApis from 'features/user/services/apis';
import { delay, first, get } from 'lodash';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { IResponseDataBody } from 'services/response';
import { ICouponPrintDiscount } from 'services/shop/types/coupon';
import { IStaffItemData } from 'services/shop/types/staff';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import styled from 'styled-components';
import toast from 'utils/toast';
type Props = {};
type HomeCouponMarketingModalRef = {
  open: (data?: IStaffItemData) => void;
  close: () => void;
};
export const useHomeCouponMarketingModal = () => useRef<HomeCouponMarketingModalRef>(null);
const HomeCouponMarketingModal = forwardRef<HomeCouponMarketingModalRef, Props>((_props, ref) => {
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [staff, setStaff] = useState<IStaffItemData | null>(null);
  const [couponData, setCoupons] = useState<ICouponPrintDiscount | null>(null);
  const [amount, setAmount] = useState(1);
  const printRef = useRef<HTMLDivElement>(null);
  const printIndex = useRef(0);
  const [isPrinting, setIsPrinting] = useState(false);
  const setLoadingPage = useSetLoadingPage();
  const couponPrintDataRef = useRef<ICouponPrintDiscount[]>([]);
  const [currentCouponPrintData, setCurrentCouponPrintData] = useState<ICouponPrintDiscount | null>(null);
  const onOpen: HomeCouponMarketingModalRef['open'] = async (_staff) => {
    setStaff(_staff || null);
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<ICouponPrintDiscount[]> = await userApis.getCouponMarketing();
      const data = res.data.data;
      if (data) {
        const result = first(data.map(o => ({
          ...o,
          itemDesc: o.itemDesc || o.description.split('\r\n') || [],
        })));
        setCoupons(result || null);
        setAmount(1);
        setTimeout(() => inputRef.current?.focus(), 100);
        setOpen(true);
        setIsPrinting(false);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Get coupon marketing failed');
    } finally {
      setLoadingPage(false);
    }
  };

  const onClose = () => {
    setOpen(false);
  };

  const oPrintMultiple = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      if (printIndex.current < amount - 1) {
        printIndex.current++;
        setCurrentCouponPrintData(couponPrintDataRef.current?.[printIndex.current]);
        delay(oPrintMultiple, 200);
      } else {
        setIsPrinting(false);
        setLoadingPage(false);
        onClose();
      }
    },
  });

  const onSubmit = async () => {
    printIndex.current = 0;
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<ICouponPrintDiscount[]> = await userApis.printCouponMarketing({
        staffId: staff?.id || '',
        couponIds: [couponData?.id || ''],
        totalPrint: amount,
      });
      const data = res.data.data;
      if (data) {
        couponPrintDataRef.current = data;
        setCurrentCouponPrintData(data[0]);
        delay(oPrintMultiple, 500);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Print coupon marketing failed');
    } finally {
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  return (
    <Modal
      visible={open}
      onClose={onClose}
      width={'auto'}
      v2
      modalTitle='print coupon'
      noneBodyStyle={true}
      onSubmit={onSubmit}
      disableOk={amount <= 0}
      className={isPrinting ? 'display-none-modal' : ''}
      okTitle='OK'

    >
      <FormStyled layout='vertical'>
        <Container>
          <CouponViewStyled>
            <PrintBodyCouponOnly coupons={couponData ? [couponData] : []} />
          </CouponViewStyled>
          {currentCouponPrintData && <div style={{ display: 'none' }}>
            <PrintBodyCouponOnly key={currentCouponPrintData?.couponCode} billRef={printRef} coupons={currentCouponPrintData ? [currentCouponPrintData] : []} />
          </div>}
          <GiftCardNumberContainer>
            <Text className='modal-label' mb={0.5}>
              Amount
            </Text>
            <div className="input-selector-container">
              <>
                <Input
                  ref={inputRef}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className='gift-card-number-input' size='small'
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
              </>
            </div>
            <>
              <NumberPadV2
                type={'NUMBER'}
                controlRef={inputRef}
                value={amount?.toString() || ''}
                onChange={(e) => setAmount(Number(e))}
              />
            </>
          </GiftCardNumberContainer>
        </Container>
      </FormStyled>
    </Modal>
  );
});

HomeCouponMarketingModal.displayName = 'HomeCouponMarketingModal';
export default HomeCouponMarketingModal;

const FormStyled = styled(Form)`
background: #F5F5F5;
margin-bottom: 6px;
`;

const Container = styled.div`
  background: #F5F5F5;
  display: flex;
`;

const CouponViewStyled = styled.div`
display: flex;
padding: 16px;
justify-content: center;
align-items: flex-start;
align-self: stretch;
background: #F5F5F5;
.PrintBodyCouponOnlyContainer {
  padding: 0;
  align-self: stretch;
  justify-content: center;
  align-items: center;
  .TicketBox {
    padding: 16px 8px;
  }
}
`;

const GiftCardNumberContainer = styled.div`
display: flex;
flex-direction: column;  
background: #fff;
padding-top: 16px;
align-self: stretch;
.modal-label {
  color: #64748B;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.input-selector-container {
  display: flex;
  align-items: center;
  position: relative;
  height: 64px;
  margin: 0 40px;
}
.gift-card-number-input {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  align-self: stretch;
  font-size: 40px;
  height: unset;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border: none;
  outline: none;
  box-shadow: none;
  &.fs-big {
    font-size: 55px;
  }
  &.fs-medium {
    font-size: 45px;
  }
}

`;
